import type { FC } from "react"
import { useMemo } from "react"
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react"
import capitalize from "lodash.capitalize"

import { shortenSupport } from "common/components/charts/helpers"
import { type Maybe, type VoteStats, type Vote, VoteType } from "query/graphql"
import { useD3BubbleChart } from "proposal/hooks/useD3BubbleChart"
import EmptyEntity from "common/components/EmptyEntity"
import { formatNumberWithPrecisionThreshold } from "common/helpers/number"
import type { VoteStyle } from "common/helpers/voteStyle"
import TallyBlack from "ui/components/icons/tally/TallyBlack"

type BubbleChartProps = {
  decimals: number
  votes?: Vote[]
  voteStats?: Maybe<VoteStats[]>
  voteStyles: Record<string, VoteStyle>
  onClickVoterBubble?: (address: string) => void
}

const BubbleChart: FC<BubbleChartProps> = ({
  decimals,
  votes,
  voteStats,
  voteStyles,
  onClickVoterBubble,
}) => {
  const shouldDisplayEmptyState = !votes?.length || !voteStats

  return (
    <Flex direction="column" justify="space-between" p={6} pb={0} w="full">
      {shouldDisplayEmptyState ? (
        <Flex align="center" direction="column" justify="center" my="6">
          <EmptyEntity title="No votes yet" />
          <Flex align="right" justify="end" pr={1} width="100%">
            <Icon as={TallyBlack} color="gray.500" h={20} w={20} />
          </Flex>
        </Flex>
      ) : (
        <>
          <Key voteStats={voteStats} voteStyles={voteStyles} />
          <Chart
            decimals={decimals}
            voteStyles={voteStyles}
            votes={votes}
            onClickVoterBubble={onClickVoterBubble}
          />
        </>
      )}
    </Flex>
  )
}

export default BubbleChart

type KeyProps = {
  voteStats?: Maybe<VoteStats[]>
  voteStyles: Record<string, VoteStyle>
}

const Key: FC<KeyProps> = ({ voteStyles, voteStats }) => {
  if (!voteStats?.length) {
    return null
  }

  return (
    <Grid
      columnGap={18}
      mb={6}
      rowGap={6}
      templateColumns="repeat(4, 1fr)"
      templateRows="repeat(auto-fill, 1.5rem)"
    >
      {voteStats
        .filter(({ type }) =>
          [VoteType.Abstain, VoteType.Against, VoteType.For].includes(type),
        )
        .map(({ percent, type }) => {
          const { color } = voteStyles[type]

          const label = capitalize(type)
          const percentLabel = `${formatNumberWithPrecisionThreshold(
            Number(percent),
          )}%`

          return (
            <Flex
              key={`ballot_type_legend_${type}_${percent}`}
              align="center"
              justify="space-between"
              minW={130}
            >
              <Stack align="center" direction="row" spacing={2}>
                <Box bg={color} h={3} rounded="full" w={3} />
                <Tooltip label={label}>
                  <Text color="gray.600" textStyle="body.bold.md">
                    {shortenSupport(label)}
                  </Text>
                </Tooltip>
                <Text color="gray.500" textStyle="body.regular.md">
                  {percentLabel}
                </Text>
              </Stack>
            </Flex>
          )
        })}
    </Grid>
  )
}

type ChartProps = {
  votes: Vote[]
  voteStyles: Record<string, VoteStyle>
  decimals: number
  onClickVoterBubble?: (address: string) => void
}

const Chart: FC<ChartProps> = ({
  votes,
  voteStyles,
  decimals,
  onClickVoterBubble,
}) => {
  const dataset = useMemo(() => votes, [votes])

  const { ref, buttonRef, zoomInBtn, zoomOutBtn } = useD3BubbleChart({
    dataset,
    decimals,
    voteStyles,
    onClickVoterBubble,
  })

  return (
    <Stack h="720px" spacing={2}>
      <svg ref={ref} height="730px" style={{ maxWidth: "100%" }} />
      <Flex align="center" justify="end" pr={1}>
        <Icon as={TallyBlack} color="gray.500" h={20} w={20} />
      </Flex>
      <HStack justify="space-between">
        <Button ref={buttonRef} variant="secondary">
          Reset
        </Button>
        <HStack spacing={4}>
          <Button ref={zoomOutBtn} variant="secondary">
            Zoom out
          </Button>
          <Button ref={zoomInBtn} variant="secondary">
            Zoom in
          </Button>
        </HStack>
      </HStack>
    </Stack>
  )
}
